import { useCallback, useState } from 'react';

import api from './api';
import { ApiEnvelope, CheckoutSession, Plan, Subscription } from '../models';
import { unwrapApiData } from '../utils/planUtils';

export const useSubscription = () => {
  const [loading, setLoading] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);
  const [creatingCheckout, setCreatingCheckout] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);

  const getPlans = useCallback((): Promise<Plan[]> => {
    setLoading(true);
    return api
      .get<ApiEnvelope<{ plans: Plan[] }>>('/v1/plans/')
      .then((response) => {
        const data = unwrapApiData(response);
        setPlans(data.plans);
        setLoading(false);
        return data.plans;
      })
      .catch((error) => {
        setLoading(false);
        return Promise.reject(error);
      });
  }, []);

  const getSubscription = useCallback((): Promise<Subscription> => {
    setLoading(true);
    return api
      .get<ApiEnvelope<Subscription>>('/v1/subscription/')
      .then((response) => {
        const data = unwrapApiData(response);
        setSubscription(data);
        setLoading(false);
        return data;
      })
      .catch((error) => {
        setLoading(false);
        return Promise.reject(error);
      });
  }, []);

  const changePlan = useCallback((planSlug: string): Promise<Subscription> => {
    setChangingPlan(true);
    return api
      .post<ApiEnvelope<Subscription>>('/v1/subscription/change/', {
        plan_slug: planSlug,
      })
      .then((response) => {
        const data = unwrapApiData(response);
        setSubscription(data);
        setChangingPlan(false);
        return data;
      })
      .catch((error) => {
        setChangingPlan(false);
        return Promise.reject(error);
      });
  }, []);

  const createCheckout = useCallback(
    (planSlug: string): Promise<CheckoutSession> => {
      setCreatingCheckout(true);
      const origin = window.location.origin;
      return api
        .post<ApiEnvelope<CheckoutSession>>('/v1/billing/checkout/', {
          plan_slug: planSlug,
          success_url: `${origin}/admin/subscription?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
          failure_url: `${origin}/admin/subscription?checkout=failure`,
        })
        .then((response) => {
          const data = unwrapApiData(response);
          setCreatingCheckout(false);
          return data;
        })
        .catch((error) => {
          setCreatingCheckout(false);
          return Promise.reject(error);
        });
    },
    []
  );

  const getCheckoutStatus = useCallback(
    (sessionId: string): Promise<CheckoutSession> => {
      return api
        .get<ApiEnvelope<CheckoutSession>>(`/v1/billing/checkout/${sessionId}/`)
        .then((response) => unwrapApiData(response));
    },
    []
  );

  const loadPlanManagement = useCallback((): Promise<void> => {
    setLoading(true);
    return Promise.all([
      api.get<ApiEnvelope<{ plans: Plan[] }>>('/v1/plans/'),
      api.get<ApiEnvelope<Subscription>>('/v1/subscription/'),
    ])
      .then(([plansResponse, subscriptionResponse]) => {
        const plansData = unwrapApiData(plansResponse);
        const subscriptionData = unwrapApiData(subscriptionResponse);
        setPlans(plansData.plans);
        setSubscription(subscriptionData);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        return Promise.reject(error);
      });
  }, []);

  return {
    loading,
    changingPlan,
    creatingCheckout,
    plans,
    subscription,
    getPlans,
    getSubscription,
    changePlan,
    createCheckout,
    getCheckoutStatus,
    loadPlanManagement,
  };
};
